'use client';

import { matchDistrict } from '@/lib/geo/match';
import { readGeoCache, writeGeoCache } from '@/lib/geo/storage';
import { wclApiService } from '@/services/wcl-api.service';

let hydrateInFlight: Promise<void> | null = null;

/**
 * If the geo cache has a state but no district, try to rematch
 * `rawDistrictName` (or known division aliases) against WCL districts and
 * upgrade the cache in place. Safe to call repeatedly — no-ops when already
 * complete or when no raw name is available.
 */
export function hydrateGeoDistrictIfNeeded(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (hydrateInFlight) return hydrateInFlight;

  const cached = readGeoCache();
  if (!cached?.stateId || cached.districtId || !cached.rawDistrictName) {
    return Promise.resolve();
  }

  hydrateInFlight = (async () => {
    try {
      const districts = await wclApiService.getDistricts('', cached.stateId!);
      const matched = matchDistrict(cached.rawDistrictName!, districts);
      if (!matched) return;

      writeGeoCache({
        ...cached,
        districtId: matched.id,
        districtName: matched.name,
      });
    } catch (error) {
      console.error('Failed to hydrate geo district from cache:', error);
    } finally {
      hydrateInFlight = null;
    }
  })();

  return hydrateInFlight;
}
